import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styles from './FAQ.module.scss';
import FAQSearchBar from './FAQSearchBar';
import PopularQuestion from './PopularQuestion';
import FAQCategoriesCard from './FAQCategoriesCard';

import useFetchFAQAllQuestions from '../../hooks/useApi/useFetchFAQAllQuestions';
import useFetchFAQCategories from '../../hooks/useApi/useFetchFAQCategories';
import useDataFetchIndicator from '../../hooks/useDataFetchIndicator';
import { useTranslation } from 'react-i18next';

const { Title, SubTitle, Button } = vp;

type FC = FunctionComponent;

type FAQCategoriesCards = {
  title: string;
  position: string;
  info: string[];
  logo: string;
};

const FAQ: FC = () => {
  const history = useHistory();
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [allQuestions, fetchFAQAllQuestions] = useFetchFAQAllQuestions({});
  const [categories, fetchFAQCategories] = useFetchFAQCategories({});
  const [popularQuestions, setPopularQuestions] = useState<vp.FAQAllQuestions[]>([]);
  const [faqCategories, setFaqCategories] = useState<FAQCategoriesCards[]>([]);
  const { t } = useTranslation('translations', { keyPrefix: 'LearningCentre.FAQ' });

  useEffect(() => {
    fetchFAQAllQuestions();
    fetchFAQCategories();
  }, []);

  useEffect(() => {
    if (allQuestions.Success) {
      const sortedQuestions = [...Object.values(allQuestions.FAQAllQuestions)];
      sortedQuestions.sort((a, b) => parseInt(b.ClickCount, 10) - parseInt(a.ClickCount, 10));
      setPopularQuestions(sortedQuestions.slice(0, 8));
    }
  }, [allQuestions]);

  useEffect(() => {
    if (categories.Success) {
      Object.values(categories.FAQCategories).map((category) => {
        const convertedInfo = category.Description.split('|').map((item) => item.trim());
        setFaqCategories((prevCategory) => [
          ...prevCategory,
          {
            title: category.Category,
            position: category.Position,
            info: convertedInfo,
            logo: category.IconBase64,
          },
        ]);
      });
    }
  }, [categories]);

  useEffect(() => {
    if (popularQuestions.length !== 0 && faqCategories.length !== 0) {
      setDataFetchingInfo(false, 'Loading...', 'fullScreen');
    } else {
      const interval = setInterval(() => {
        setDataFetchingInfo(false, 'Loading...', 'fullScreen');
        setDataFetchingInfo(true, 'Loading...', 'fullScreen');
      }, 150);
      return () => {
        clearInterval(interval);
      };
    }
    return () => {};
  }, [popularQuestions, faqCategories]);

  return (
    <div className={styles.faqContainer}>
      <div className={styles.helpContainer}>
        <div className={styles.helpContainer__innerWrapper}>
          <Title title={t('title')} textSize="28px" center />
          <div className={styles.searchContainer}>
            <FAQSearchBar position="center" borderColor="none" searchedValue="" />
          </div>
        </div>
      </div>
      <div className={styles.popularQuestionsContainer}>
        <SubTitle subTitle={t('popularQuestionsSubTitle')} fontSize="20px" />
        <div className={styles.yellowUnderlineBar} />
        <div className={styles.popularQuestionsContainer__innerWrapper}>
          {popularQuestions.map((question) => (
            <PopularQuestion
              category={question.Category}
              subCategory={question.SubCategory}
              question={question.Question}
            />
          ))}
        </div>
      </div>
      <div className={styles.faqCategoriesContainer}>
        <SubTitle subTitle={t('FAQCategoriesSubTitle')} fontSize="20px" />
        <div className={styles.yellowUnderlineBar} />
        <div className={styles.faqCategoriesContainer__innerWrapper}>
          {faqCategories
            .sort((a, b) => parseInt(a.position) - parseInt(b.position))
            .map((category) => (
              <FAQCategoriesCard title={category.title} info={category.info} logo={category.logo} />
            ))}
        </div>
      </div>
      <div className={styles.contactSupportContainer}>
        <div className={styles.greyUnderlineBar} />
        <p>{t('cantFindHelp')}</p>
        <Button
          id="contact-support-button"
          label={t('contactSupportButton')}
          color="secondary"
          size="jumbo"
          onClick={() => {
            history.push('/create-tickets');
          }}
        />
      </div>
    </div>
  );
};
export default FAQ;
